import { Star, ExternalLink, Quote } from "lucide-react";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { useSiteT } from "../../hooks/useSiteT";

const AVATARS = [
    "https://images.unsplash.com/photo-1758600587730-a11917c13b85?crop=entropy&cs=srgb&fm=jpg&w=160&q=85",
    "https://images.unsplash.com/photo-1767175620484-1ed37931a0d1?crop=entropy&cs=srgb&fm=jpg&w=160&q=85",
    "https://images.pexels.com/photos/28513054/pexels-photo-28513054.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=160&w=160",
];

const NAMES = ["David M.", "Sarah K.", "Marcus T."];
const METRICS = ["$2M / mo", "$400k / day", "11 months"];

const COPY = {
    eyebrow: { en: "Proven results", tr: "Kanıtlanmış sonuçlar", es: "Resultados probados", pt: "Resultados comprovados", ru: "Доказанные результаты" },
    h1: { en: "Operators scale on Adrise", tr: "Operatörler Adrise'da ölçekleniyor", es: "Los operadores escalan en Adrise", pt: "Operadores escalam na Adrise", ru: "Операторы масштабируются на Adrise" },
    h2: { en: "and never switch back.", tr: "ve bir daha geri dönmüyor.", es: "y nunca vuelven atrás.", pt: "e nunca voltam atrás.", ru: "и больше не возвращаются." },
    sub: { en: "Real teams moving 7–8 figures a month. Verified reviews — not stock quotes.", tr: "Aylık 7–8 haneli hacim yöneten gerçek ekipler. Doğrulanmış yorumlar — stok cümleler değil.", es: "Equipos reales que mueven 7–8 cifras al mes. Reseñas verificadas, no frases genéricas.", pt: "Equipes reais movimentando 7–8 dígitos por mês. Avaliações verificadas — não frases prontas.", ru: "Реальные команды с оборотом 7–8 знаков в месяц. Проверенные отзывы, а не шаблоны." },
    excellent: { en: "Rated Excellent", tr: "Mükemmel puanlandı", es: "Calificado Excelente", pt: "Avaliado como Excelente", ru: "Оценка «Отлично»" },
    reviews: { en: "verified reviews on Trustpilot", tr: "Trustpilot'ta doğrulanmış yorum", es: "reseñas verificadas en Trustpilot", pt: "avaliações verificadas no Trustpilot", ru: "проверенных отзывов на Trustpilot" },
    readAll: { en: "Read all reviews", tr: "Tüm yorumları oku", es: "Ver todas las reseñas", pt: "Ver todas as avaliações", ru: "Все отзывы" },
    result: { en: "Result", tr: "Sonuç", es: "Resultado", pt: "Resultado", ru: "Результат" },
};

const REVIEWS = {
    en: [
        { tag: "migrated · zero downtime", quote: "We moved $2M/month from three providers to Adrise. Zero downtime, zero bans, instant replacements — our CFO is genuinely happy.", role: "Founder · 9-figure DTC brand" },
        { tag: "on crypto credit line", quote: "The crypto credit line transformed our cashflow. We now push $400k/day without ever touching a corporate card.", role: "Performance Director · Media Agency" },
        { tag: "stable · zero bans", quote: "We tested five agencies before Adrise. None survived past week two. Adrise has stayed stable for 11 months straight.", role: "8-figure Media Buyer" },
    ],
    tr: [
        { tag: "taşındı · sıfır kesinti", quote: "Aylık $2M hacmi üç sağlayıcıdan Adrise'a taşıdık. Sıfır kesinti, sıfır ban, anında değişim — CFO'muz gerçekten mutlu.", role: "Kurucu · 9-figure DTC marka" },
        { tag: "kripto credit line ile", quote: "Kripto credit line nakit akışımızı tamamen değiştirdi. Artık kurumsal karta hiç dokunmadan günde $400k spend yönetiyoruz.", role: "Performance Director · Medya Ajansı" },
        { tag: "stabil · sıfır ban", quote: "Adrise öncesi 5 ajans denedik. Hiçbiri 2 haftayı geçemedi. Adrise hesapları 11 aydır kesintisiz stabil.", role: "8-figure Media Buyer" },
    ],
    es: [
        { tag: "migrado · cero caídas", quote: "Movimos $2M/mes de tres proveedores a Adrise. Cero caídas, cero baneos y reemplazos instantáneos — nuestro CFO está feliz.", role: "Fundador · marca DTC de 9 cifras" },
        { tag: "con línea de crédito cripto", quote: "La línea de crédito en cripto transformó nuestro flujo de caja. Hoy movemos $400k/día sin tocar una tarjeta corporativa.", role: "Performance Director · Agencia de medios" },
        { tag: "estable · cero baneos", quote: "Probamos cinco agencias antes de Adrise. Ninguna pasó de la segunda semana. Adrise lleva 11 meses estable.", role: "Media Buyer de 8 cifras" },
    ],
    pt: [
        { tag: "migrado · zero downtime", quote: "Movemos $2M/mês de três fornecedores para a Adrise. Zero downtime, zero bans e substituições instantâneas — nosso CFO está feliz.", role: "Fundador · marca DTC de 9 dígitos" },
        { tag: "com linha de crédito cripto", quote: "A linha de crédito em cripto transformou nosso fluxo de caixa. Hoje rodamos $400k/dia sem tocar em cartão corporativo.", role: "Performance Director · Agência de mídia" },
        { tag: "estável · zero bans", quote: "Testamos cinco agências antes da Adrise. Nenhuma passou da segunda semana. A Adrise está estável há 11 meses.", role: "Media Buyer de 8 dígitos" },
    ],
    ru: [
        { tag: "перенос · ноль простоев", quote: "Мы перенесли $2M/мес от трёх провайдеров в Adrise. Ноль простоев, ноль банов, мгновенные замены — наш CFO доволен.", role: "Основатель · DTC-бренд, 9 знаков" },
        { tag: "на крипто-кредитной линии", quote: "Крипто-кредитная линия изменила наш кэшфлоу. Теперь откручиваем $400k/день, не трогая корпоративные карты.", role: "Performance Director · медиаагентство" },
        { tag: "стабильно · ноль банов", quote: "До Adrise мы протестировали пять агентств. Ни одно не продержалось дольше двух недель. Adrise стабилен 11 месяцев.", role: "Медиабайер, 8 знаков" },
    ],
};

export default function ResultsShowcase() {
    const { settings } = useSiteSettings();
    const { pick } = useSiteT();
    const rating = settings.trustpilot_rating || 4.9;
    const count = settings.trustpilot_count || 320;
    const t = (k) => COPY[k]?.[pick({ en: "en", tr: "tr", es: "es", pt: "pt", ru: "ru" })] || COPY[k]?.en;
    const items = (pick(REVIEWS) || REVIEWS.en).map((x, i) => ({
        ...x, name: NAMES[i], avatar: AVATARS[i], metric: METRICS[i],
    }));

    return (
        <section
            id="results"
            data-testid="results-showcase"
            className="py-24 md:py-32 border-y border-white/[0.06] relative overflow-hidden"
        >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(158 42% 46% / 0.5), transparent)" }} />
            <div className="container-skala">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-14 md:mb-16">
                    <div className="lg:col-span-7 space-y-5 reveal-up">
                        <p className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">
                            <span className="w-1.5 h-1.5 rounded-full bg-highlight" /> {t("eyebrow")}
                        </p>
                        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                            {t("h1")}{" "}
                            <span className="serif-accent text-foreground/80">{t("h2")}</span>
                        </h2>
                        <p className="text-muted-foreground max-w-xl text-base md:text-lg">{t("sub")}</p>
                    </div>

                    {/* Trustpilot rating block */}
                    <div className="lg:col-span-5 reveal-up" style={{ animationDelay: "0.08s" }}>
                        <a
                            href="https://www.trustpilot.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="results-trustpilot-link"
                            className="block bg-card border border-white/[0.08] hover:border-[#00b67a]/40 rounded-2xl p-6 md:p-7 group transition-colors"
                        >
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-1 text-[#00b67a] mb-3">
                                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-display font-black text-5xl md:text-6xl tabular-nums leading-none">{rating}</span>
                                        <span className="text-sm text-muted-foreground">/ 5</span>
                                    </div>
                                    <p className="text-xs font-mono uppercase tracking-[0.16em] text-[#00b67a] mt-3">{t("excellent")}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-display font-black text-3xl md:text-4xl tabular-nums">{count}+</div>
                                    <p className="text-[11px] text-muted-foreground leading-snug mt-1 max-w-[9rem]">{t("reviews")}</p>
                                </div>
                            </div>
                            <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between">
                                <span className="font-display font-bold text-sm text-[#00b67a]">★ Trustpilot</span>
                                <span className="inline-flex items-center gap-1.5 text-xs text-foreground/70 group-hover:text-foreground transition-colors">
                                    {t("readAll")}
                                    <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </span>
                            </div>
                        </a>
                    </div>
                </div>

                {/* Curated reviews */}
                <div className="grid md:grid-cols-3 gap-5 md:gap-6">
                    {items.map((x, i) => (
                        <figure
                            key={i}
                            data-testid={`result-review-${i}`}
                            className="reveal-up relative bg-card border border-white/[0.07] hover:border-highlight/30 rounded-2xl p-7 flex flex-col"
                            style={{ animationDelay: `${0.12 + i * 0.08}s` }}
                        >
                            <div className="flex items-start justify-between gap-3 mb-5">
                                <div className="inline-flex flex-col">
                                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">{t("result")}</span>
                                    <span className="font-display font-black text-2xl text-highlight tabular-nums leading-none">{x.metric}</span>
                                    <span className="text-[11px] font-mono text-highlight/70 mt-1.5">{x.tag}</span>
                                </div>
                                <Quote className="w-7 h-7 text-white/[0.08] shrink-0" />
                            </div>
                            <div className="flex items-center gap-1 text-[#00b67a] mb-4">
                                {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" />)}
                            </div>
                            <blockquote className="font-display text-lg leading-relaxed text-foreground/90 flex-1">“{x.quote}”</blockquote>
                            <figcaption className="flex items-center gap-3 mt-6 pt-5 border-t border-white/[0.06]">
                                <img src={x.avatar} alt={x.name} loading="lazy" className="w-10 h-10 rounded-full object-cover border border-white/10" />
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold truncate">{x.name}</p>
                                    <p className="text-[11px] font-mono text-muted-foreground tracking-wide truncate">{x.role}</p>
                                </div>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
